/**
 * /projects/:projectSlug/envs/:envSlug/secrets — secrets list for one env.
 *
 * Project + env come from the URL, not from a global picker context. The
 * scope is fetched (mostly to surface a "not found" state if the slugs in
 * the URL don't resolve) alongside the secrets list itself.
 */

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type SecretListItem,
  type Visibility,
  WardkeyAPIError,
  useActiveOrg,
  useApiMutation,
  useApiQuery,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Layers } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";

export function SecretsRoute() {
  const activeOrg = useActiveOrg();
  const params = useParams<{ projectSlug: string; envSlug: string }>();
  const projectSlug = params.projectSlug ?? "";
  const envSlug = params.envSlug ?? "";

  const enabled = projectSlug.length > 0 && envSlug.length > 0;

  // Confirms the URL scope resolves (404 → not-found state).
  const env = useApiQuery(
    ["env", activeOrg.org_id, projectSlug, envSlug],
    (client) => client.envs.get(activeOrg.org_id, projectSlug, envSlug),
    { enabled, suppressToast: true },
  );

  // Keyed by the URL so it loads in parallel with the env check — both land
  // behind one skeleton instead of chaining env → list (the prior double flash).
  const list = useApiQuery(
    ["secrets", activeOrg.org_id, projectSlug, envSlug],
    (client) => client.secrets.list(activeOrg.org_id, projectSlug, envSlug),
    { enabled, suppressToast: true },
  );

  // Only the initial load gets the skeleton / error page — `isLoading` flips
  // back to true on every refetch (e.g. after a delete) while `data` is kept,
  // so gating on `!data` avoids re-flashing the whole page.
  if (env.error && !env.data) {
    return <ScopeErrorState error={env.error} projectSlug={projectSlug} />;
  }
  if (!env.data || (list.isLoading && !list.data)) {
    return <SecretsPageSkeleton />;
  }

  return (
    <ScopedSecretsList
      orgId={activeOrg.org_id}
      orgName={activeOrg.name}
      projectSlug={projectSlug}
      envSlug={envSlug}
      secrets={list.data?.secrets ?? []}
      listError={list.error}
      onRefetch={() => list.refetch()}
    />
  );
}

function ScopedSecretsList({
  orgId,
  orgName,
  projectSlug,
  envSlug,
  secrets,
  listError,
  onRefetch,
}: {
  orgId: string;
  orgName: string;
  projectSlug: string;
  envSlug: string;
  secrets: ReadonlyArray<SecretListItem>;
  listError: Error | null;
  onRefetch: () => void;
}) {
  const [prefix, setPrefix] = useState("");

  const filtered = useMemo(
    () => (prefix ? secrets.filter((s) => s.path.startsWith(prefix)) : secrets),
    [secrets, prefix],
  );

  const newHref = `/projects/${encodeURIComponent(projectSlug)}/envs/${encodeURIComponent(envSlug)}/secrets/new`;
  const importHref = `/projects/${encodeURIComponent(projectSlug)}/envs/${encodeURIComponent(envSlug)}/secrets/import`;

  const total = secrets.length;

  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader
        title="Secrets"
        subtitle={
          <span className="font-mono text-[12px] text-muted-foreground">
            {orgName} / {projectSlug}/{envSlug}
          </span>
        }
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={importHref}>Import .env</Link>
            </Button>
            <Button asChild size="sm">
              <Link to={newHref}>
                New secret
                <span aria-hidden>→</span>
              </Link>
            </Button>
          </>
        }
      />

      <div className="scope-bar">
        <div>
          <Layers size={17} className="text-accent shrink-0" />
          <code>
            {projectSlug} / {envSlug}
          </code>
        </div>
        <span>Environment secrets</span>
      </div>
      <FilterBar
        prefix={prefix}
        onPrefixChange={setPrefix}
        totalCount={total}
        filteredCount={filtered.length}
      />

      <SecretsTable
        error={listError}
        scopeEmpty={!listError && total === 0}
        filtered={filtered}
        prefix={prefix}
        orgId={orgId}
        projectSlug={projectSlug}
        envSlug={envSlug}
        newHref={newHref}
        importHref={importHref}
        onDeleted={onRefetch}
        onRefetch={onRefetch}
      />
    </div>
  );
}

function ScopeErrorState({ error, projectSlug }: { error: Error; projectSlug: string }) {
  const isNotFound = error instanceof WardkeyAPIError && error.status === 404;
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader title="Secrets" />
      <div className="rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {isNotFound ? "not found" : "load failed"}
        </div>
        <h2 className="mt-3 text-lg font-medium text-foreground">
          {isNotFound ? "This environment doesn't exist" : "Couldn't load this environment"}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
          {isNotFound
            ? "It may have been deleted, or one of the slugs in the URL is wrong."
            : error instanceof WardkeyAPIError
              ? error.message
              : error.message || "Request failed"}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={projectSlug ? `/projects/${encodeURIComponent(projectSlug)}` : "/projects"}>
              Back to environments
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function SecretsPageSkeleton() {
  return (
    <div className="mx-auto max-w-[1200px]">
      <div className="mb-8 flex items-end justify-between border-b border-border pb-5">
        <Skeleton className="h-7 w-28" />
        <Skeleton className="h-8 w-40" />
      </div>
      <div className="grid gap-px">
        {Array.from({ length: 5 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no stable id
          <Skeleton key={i} className="h-11 w-full rounded-md" />
        ))}
      </div>
    </div>
  );
}

function FilterBar({
  prefix,
  onPrefixChange,
  totalCount,
  filteredCount,
}: {
  prefix: string;
  onPrefixChange: (next: string) => void;
  totalCount: number;
  filteredCount: number;
}) {
  const hidden = totalCount - filteredCount;
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <label className="flex min-w-0 w-full max-w-md flex-1 items-center gap-2 rounded-md border border-input bg-card px-3 py-2.5 transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
        <span
          aria-hidden
          className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground/60"
        >
          path/
        </span>
        <input
          type="text"
          value={prefix}
          onChange={(e) => onPrefixChange(e.target.value)}
          placeholder="Filter by path prefix"
          aria-label="Filter by path prefix"
          className="w-full bg-transparent font-mono text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        {prefix && (
          <button
            type="button"
            onClick={() => onPrefixChange("")}
            aria-label="Clear filter"
            className="rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            clear
          </button>
        )}
      </label>
      <div
        aria-live="polite"
        className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
      >
        {`${filteredCount} shown${hidden > 0 ? ` · ${hidden} hidden` : ""}`}
      </div>
    </div>
  );
}

const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_4rem_7rem_10rem] items-center gap-x-4";

function SecretsTable({
  error,
  scopeEmpty,
  filtered,
  prefix,
  orgId,
  projectSlug,
  envSlug,
  newHref,
  importHref,
  onDeleted,
  onRefetch,
}: {
  error: Error | null;
  scopeEmpty: boolean;
  filtered: ReadonlyArray<SecretListItem>;
  prefix: string;
  orgId: string;
  projectSlug: string;
  envSlug: string;
  newHref: string;
  importHref: string;
  onDeleted: () => void;
  onRefetch: () => void;
}) {
  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
          load failed
        </div>
        <p className="mt-2 text-[14px] text-foreground">
          {error instanceof WardkeyAPIError ? error.message : error.message || "Request failed"}
        </p>
        <Button variant="outline" size="sm" onClick={onRefetch} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  if (scopeEmpty) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/40 p-12 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          nothing here yet
        </div>
        <h2 className="mt-3 text-lg font-medium text-foreground">No secrets yet</h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
          Store your first API key, database URL, or signing secret. Wardkey encrypts it at rest,
          versions every change, and audits every access.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Button asChild size="sm">
            <Link to={newHref}>
              Create your first secret
              <span aria-hidden>→</span>
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to={importHref}>Or import a .env block</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card/40 p-8 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          no matches
        </div>
        <p className="mt-2 text-[13px] text-muted-foreground">
          No secrets match prefix{" "}
          <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-foreground">
            {prefix}
          </code>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="secrets-table">
      {/* Column header — not a row; purely a ledger label strip. */}
      <div
        className={`${ROW_GRID} border-b border-border bg-muted/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70`}
      >
        <span>path</span>
        <span className="hidden text-right lg:block">ver</span>
        <span className="hidden lg:block">created</span>
        <span aria-hidden />
      </div>
      <ul className="divide-y divide-border">
        {filtered.map((s) => (
          <SecretRow
            key={s.path}
            secret={s}
            orgId={orgId}
            projectSlug={projectSlug}
            envSlug={envSlug}
            onDeleted={onDeleted}
          />
        ))}
      </ul>
    </div>
  );
}

function SecretRow({
  secret,
  orgId,
  projectSlug,
  envSlug,
  onDeleted,
}: {
  secret: SecretListItem;
  orgId: string;
  projectSlug: string;
  envSlug: string;
  onDeleted: () => void;
}) {
  const tier = secret.visibility;
  const { pushToast } = useToast();
  const [confirming, setConfirming] = useState(false);

  const remove = useApiMutation<void, void>(
    (c) => c.secrets.remove(orgId, projectSlug, envSlug, secret.path),
    {
      suppressToast: true,
      onSuccess: () => {
        setConfirming(false);
        pushToast({ kind: "success", title: `Deleted ${secret.path}` });
        onDeleted();
      },
      onError: (err) => {
        setConfirming(false);
        pushToast({
          kind: "error",
          title: `Couldn't delete ${secret.path}`,
          body: err instanceof WardkeyAPIError ? err.message : err.message,
        });
      },
    },
  );

  async function copyCli() {
    // Mirror the CLI surface: `wardkey secrets get <path>` reads active context
    // from ~/.wardkey/config; explicit flags make the copied command work even
    // from a fresh shell.
    const cmd = `wardkey secrets get ${secret.path} --org ${orgId} --project ${projectSlug} --env ${envSlug}`;
    try {
      await navigator.clipboard.writeText(cmd);
      pushToast({ kind: "success", title: "Copied CLI command" });
    } catch {
      pushToast({ kind: "error", title: "Clipboard copy failed" });
    }
  }

  const detailHref = `/projects/${encodeURIComponent(projectSlug)}/envs/${encodeURIComponent(envSlug)}/secrets/${encodeURIComponent(secret.path)}`;

  return (
    <li className={`group ${ROW_GRID} px-4 py-2.5 transition-colors hover:bg-muted/40`}>
      <div className="secret-name flex min-w-0 items-center gap-x-2.5 gap-y-1.5">
        <Link
          to={detailHref}
          className="truncate font-mono text-[13px] text-foreground underline-offset-4 hover:underline"
        >
          {secret.path}
        </Link>
        {tier && <VisibilityChip tier={tier} />}
        {/* Mobile-only meta, since the version/updated columns collapse. */}
        <span className="ml-auto shrink-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 lg:hidden">
          v{secret.current_version}
        </span>
      </div>

      <div className="hidden text-right font-mono text-[11px] text-muted-foreground lg:block">
        v{secret.current_version}
      </div>
      <div
        className="hidden font-mono text-[11px] uppercase tracking-widest text-muted-foreground/70 lg:block"
        title={new Date(secret.created_at * 1000).toLocaleString()}
      >
        {formatRelative(secret.created_at)}
      </div>

      <div className="justify-self-end">
        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="hidden font-mono text-[10px] uppercase tracking-widest text-destructive sm:inline">
              delete?
            </span>
            <Button
              type="button"
              variant="destructive"
              size="xs"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Confirm"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setConfirming(false)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={copyCli}
              aria-label={`Copy CLI command for ${secret.path}`}
            >
              CLI
            </Button>
            <Button asChild variant="ghost" size="xs">
              <Link to={detailHref}>Open</Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setConfirming(true)}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              Delete
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function VisibilityChip({ tier }: { tier: Visibility }) {
  const toneClass =
    tier === "sealed"
      ? "border-border bg-muted text-foreground"
      : tier === "open"
        ? "border-border bg-transparent text-muted-foreground/70"
        : "border-border bg-transparent text-muted-foreground";
  return (
    <span
      className={`shrink-0 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${toneClass}`}
      aria-label={`visibility: ${tier}`}
    >
      {tier}
    </span>
  );
}

function formatRelative(epochSeconds: number): string {
  const deltaSec = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (deltaSec < 60) return "just now";
  const m = Math.floor(deltaSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}
